// Resume <-> Job Description matching.
// Runs fully in the browser so a score can be computed when a candidate applies
// and recomputed in the dashboard whenever a JD is edited.

// Canonical skill -> aliases that may appear in a resume or JD.
export const SKILL_ALIASES = {
  javascript: ["javascript", "js", "es6", "ecmascript"],
  typescript: ["typescript", "ts"],
  react: ["react", "reactjs", "react.js"],
  "react native": ["react native", "react-native"],
  "next.js": ["next.js", "nextjs", "next js"],
  redux: ["redux", "redux toolkit", "rtk"],
  angular: ["angular", "angularjs", "angular.js"],
  vue: ["vue", "vuejs", "vue.js", "nuxt"],
  "node.js": ["node.js", "nodejs", "node js", "node"],
  express: ["express", "expressjs", "express.js"],
  nestjs: ["nestjs", "nest.js"],
  graphql: ["graphql", "apollo"],
  "rest api": ["rest api", "restful", "rest apis"],
  mongodb: ["mongodb", "mongo", "mongoose"],
  postgresql: ["postgresql", "postgres", "psql"],
  mysql: ["mysql"],
  redis: ["redis"],
  elasticsearch: ["elasticsearch", "elastic search", "opensearch"],
  firebase: ["firebase", "firestore"],
  html: ["html", "html5"],
  css: ["css", "css3", "scss", "sass", "tailwind", "tailwindcss", "bootstrap"],
  flutter: ["flutter"],
  dart: ["dart"],
  android: ["android", "android sdk"],
  kotlin: ["kotlin"],
  java: ["java"],
  swift: ["swift", "swiftui"],
  ios: ["ios", "xcode"],
  python: ["python"],
  django: ["django"],
  fastapi: ["fastapi"],
  flask: ["flask"],
  go: ["golang", "go lang"],
  aws: ["aws", "amazon web services", "ec2", "lambda", "s3"],
  gcp: ["gcp", "google cloud"],
  azure: ["azure"],
  docker: ["docker", "containers"],
  kubernetes: ["kubernetes", "k8s"],
  "ci/cd": ["ci/cd", "cicd", "github actions", "jenkins", "gitlab ci"],
  git: ["git", "github", "gitlab", "bitbucket"],
  "machine learning": ["machine learning", "ml"],
  "deep learning": ["deep learning", "neural network", "neural networks"],
  llm: ["llm", "llms", "large language model", "large language models", "gpt", "openai", "claude", "gemini", "llama"],
  "generative ai": ["generative ai", "genai", "gen ai"],
  rag: ["rag", "retrieval augmented generation", "retrieval-augmented generation"],
  langchain: ["langchain", "llamaindex", "llama index"],
  "vector database": ["vector database", "vector db", "pinecone", "weaviate", "chroma", "pgvector", "qdrant", "milvus"],
  "prompt engineering": ["prompt engineering", "prompting"],
  pytorch: ["pytorch", "torch"],
  tensorflow: ["tensorflow", "keras"],
  nlp: ["nlp", "natural language processing"],
  "computer vision": ["computer vision", "opencv", "yolo"],
  "data analysis": ["pandas", "numpy", "data analysis"],
  "ui/ux": ["ui/ux", "ux", "figma", "user experience"],
  testing: ["jest", "testing", "unit testing", "cypress", "playwright", "selenium"],
  agile: ["agile", "scrum", "jira"],
};

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");

export const normalizeText = (text = "") =>
  ` ${String(text).toLowerCase().replace(/[–—]/g, "-").replace(/\s+/g, " ")} `;

const aliasesFor = (skill) => {
  const key = skill.trim().toLowerCase();
  if (SKILL_ALIASES[key]) return SKILL_ALIASES[key];
  const canonical = Object.keys(SKILL_ALIASES).find((k) =>
    SKILL_ALIASES[k].includes(key)
  );
  return canonical ? SKILL_ALIASES[canonical] : [key];
};

// Word-boundary aware match so "java" does not match "javascript".
export const hasSkill = (normalized, skill) =>
  aliasesFor(skill).some((alias) =>
    new RegExp(`(^|[^a-z0-9+#])${escapeRegExp(alias)}($|[^a-z0-9+#])`).test(
      normalized
    )
  );

// Every known skill that appears in a block of text (used for chips/filters).
export const detectSkills = (text) => {
  const normalized = normalizeText(text);
  return Object.keys(SKILL_ALIASES).filter((skill) =>
    hasSkill(normalized, skill)
  );
};

const MONTHS = "jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec";

// Best-effort years of experience: explicit "5+ years" claims, else date ranges.
export const extractYearsOfExperience = (text = "") => {
  const normalized = normalizeText(text);
  let explicit = 0;
  const claim = /(\d{1,2}(?:\.\d)?)\s*\+?\s*(?:years?|yrs?)(?:\s+of)?(?:\s+\w+){0,3}\s+experience/g;
  let m;
  while ((m = claim.exec(normalized))) {
    const years = parseFloat(m[1]);
    if (years < 40) explicit = Math.max(explicit, years);
  }
  if (explicit) return explicit;

  const now = new Date();
  const range = new RegExp(
    `(?:(${MONTHS})[a-z]*\\.?\\s*)?((?:19|20)\\d{2})\\s*(?:-|to)\\s*(?:(?:(${MONTHS})[a-z]*\\.?\\s*)?((?:19|20)\\d{2})|present|current|now|till date|today)`,
    "g"
  );
  let months = 0;
  while ((m = range.exec(normalized))) {
    const startYear = parseInt(m[2], 10);
    const endYear = m[4] ? parseInt(m[4], 10) : now.getFullYear();
    const startMonth = m[1] ? MONTHS.split("|").indexOf(m[1].slice(0, 3)) : 0;
    const endMonth = m[3]
      ? MONTHS.split("|").indexOf(m[3].slice(0, 3))
      : m[4]
      ? 11
      : now.getMonth();
    const span = (endYear - startYear) * 12 + (endMonth - startMonth);
    if (span > 0 && span < 480) months += span;
  }
  return Math.round((months / 12) * 10) / 10;
};

const STOP_WORDS = new Set(
  "a an and are as at be by for from has have in is it of on or our that the this to we will with you your who what work working team role able strong good experience years year using use etc must should plus including within across ability skills knowledge looking join".split(
    " "
  )
);

const keywords = (text) =>
  new Set(
    normalizeText(text)
      .split(/[^a-z0-9+#.]+/)
      .map((w) => w.replace(/^\.+|\.+$/g, ""))
      .filter((w) => w.length > 2 && !STOP_WORDS.has(w) && !/^\d+$/.test(w))
  );

export const toSkillList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Score a resume against a job.
 * Weights: required skills 55, nice-to-have 15, experience 20, JD keyword overlap 10.
 * Returns a score 0-100 plus the breakdown shown to recruiters.
 */
export const scoreResume = (resumeText, job = {}, declaredYears) => {
  const normalized = normalizeText(resumeText);
  const required = toSkillList(job.requiredSkills);
  const nice = toSkillList(job.niceToHaveSkills);

  const matchedRequired = required.filter((s) => hasSkill(normalized, s));
  const missingRequired = required.filter((s) => !matchedRequired.includes(s));
  const matchedNice = nice.filter((s) => hasSkill(normalized, s));

  const parsedYears = extractYearsOfExperience(resumeText);
  const declared = parseFloat(declaredYears);
  const years = !isNaN(declared) && declared > 0 ? declared : parsedYears;
  const minYears = parseFloat(job.minExperience) || 0;

  const requiredScore = required.length
    ? (matchedRequired.length / required.length) * 55
    : 55;
  const niceScore = nice.length ? (matchedNice.length / nice.length) * 15 : 15;
  const experienceScore =
    minYears <= 0 ? 20 : Math.min(years / minYears, 1) * 20;

  const jdWords = keywords(
    [job.title, job.description, toSkillList(job.responsibilities).join(" ")].join(" ")
  );
  const resumeWords = keywords(resumeText);
  let overlap = 0;
  jdWords.forEach((w) => resumeWords.has(w) && overlap++);
  const keywordScore = jdWords.size
    ? Math.min(overlap / Math.min(jdWords.size, 40), 1) * 10
    : 10;

  const hasText = normalized.trim().length > 0;
  const score = hasText
    ? Math.round(requiredScore + niceScore + experienceScore + keywordScore)
    : null;

  return {
    score,
    matchedRequired,
    missingRequired,
    matchedNice,
    years,
    meetsExperience: minYears <= 0 || years >= minYears,
    breakdown: {
      required: Math.round(requiredScore),
      niceToHave: Math.round(niceScore),
      experience: Math.round(experienceScore),
      keywords: Math.round(keywordScore),
    },
  };
};

export const scoreBand = (score) => {
  if (score === null || score === undefined) return { label: "Not scored", tone: "muted" };
  if (score >= 75) return { label: "Strong match", tone: "green" };
  if (score >= 55) return { label: "Good match", tone: "blue" };
  if (score >= 35) return { label: "Partial match", tone: "yellow" };
  return { label: "Low match", tone: "red" };
};
